<template>
  <div class="assistant-view-container">
    <div v-if="!hasStarted" class="assistant-hero">
      <div class="hero-content">
        <div class="hero-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 14.5 5.5 14.5 12C14.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 9.5 5.5 9.5 12C9.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M2 12H22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M4 7H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M4 17H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <h1 class="hero-title">Atlas</h1>
        <p class="hero-subtitle">Your intelligence assistant, powered by your data</p>

        <form class="hero-form" @submit.prevent="submitInitialPrompt">
          <div class="input-wrapper">
            <textarea
              v-model="initialPrompt"
              class="hero-input"
              placeholder="Ask about campaigns, impressions, performance..."
              :disabled="starting"
              rows="1"
              @keydown.enter.exact.prevent="submitInitialPrompt"
              @input="autoResize"
              ref="heroTextarea"
            ></textarea>
            <button
              class="send-button"
              type="submit"
              :disabled="starting || !initialPrompt.trim()"
              :class="{ 'active': initialPrompt.trim() }"
            >
              <span v-if="starting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </form>

        <!-- Action Links -->
        <div class="hero-actions">
          <button type="button" class="hero-action-link" @click="showToolkitModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Open Toolkit
          </button>
          <button type="button" class="hero-action-link secondary" @click="showHelpModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            See all questions
          </button>
        </div>
      </div>
    </div>

    <div v-else class="assistant-chat-wrapper">
      <AiChatPanel
        ref="chatPanel"
        title="Atlas"
        :contextIds="[]"
        :customButtons="[]"
        @open-help="showHelpModal = true"
      />
    </div>

    <!-- Help Modal -->
    <AtlasHelpModal
      :visible="showHelpModal"
      @close="showHelpModal = false"
      @select-prompt="handleHelpPromptSelect"
    />

    <!-- Toolkit Modal -->
    <AtlasToolkitModal
      :visible="showToolkitModal"
      @close="showToolkitModal = false"
      @execute="handleToolkitExecute"
    />
  </div>
</template>

<script>
import AiChatPanel from '@/components/AiChatPanel.vue';
import AtlasHelpModal from '@/components/AtlasHelpModal.vue';
import AtlasToolkitModal from '@/components/AtlasToolkitModal.vue';

export default {
  name: 'Assistant',
  components: {
    AiChatPanel,
    AtlasHelpModal,
    AtlasToolkitModal,
  },
  data() {
    return {
      hasStarted: false,
      starting: false,
      initialPrompt: '',
      heroAvatar: 'none',
      showHelpModal: false,
      showToolkitModal: false,
      pendingRecipe: null,
    };
  },
  methods: {
    useExample(text) {
      this.initialPrompt = text;
      this.$nextTick(() => {
        if (this.$refs.heroTextarea) {
          this.$refs.heroTextarea.focus();
          this.autoResize();
        }
      });
    },
    autoResize() {
      const textarea = this.$refs.heroTextarea;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
      }
    },
    handleHelpPromptSelect(prompt) {
      if (this.hasStarted && this.$refs.chatPanel) {
        // If already in chat, inject the prompt
        if (this.$refs.chatPanel.setUserMessage) {
          this.$refs.chatPanel.setUserMessage(prompt);
        }
      } else {
        // On landing page, use the example
        this.useExample(prompt);
      }
    },
    async submitInitialPrompt() {
      if (!this.initialPrompt || !this.initialPrompt.trim() || this.starting) return;
      this.starting = true;
      this.hasStarted = true;
      this.$nextTick(async () => {
        try {
          if (this.$refs.chatPanel && this.$refs.chatPanel.startWithPrompt) {
            if (this.$refs.chatPanel.setAvatar) {
              this.$refs.chatPanel.setAvatar(this.heroAvatar);
            }
            await this.$refs.chatPanel.startWithPrompt(this.initialPrompt.trim());
          }
        } finally {
          this.initialPrompt = '';
          this.starting = false;
        }
      });
    },
    handleToolkitExecute(recipeData) {
      this.showToolkitModal = false;

      if (this.hasStarted && this.$refs.chatPanel) {
        // If already in chat, execute recipe directly
        if (this.$refs.chatPanel.handleRecipeExecute) {
          this.$refs.chatPanel.handleRecipeExecute(recipeData);
        }
      } else {
        // On landing page, start chat and then execute recipe
        this.pendingRecipe = recipeData;
        this.hasStarted = true;
        this.$nextTick(() => {
          if (this.$refs.chatPanel && this.$refs.chatPanel.handleRecipeExecute) {
            this.$refs.chatPanel.handleRecipeExecute(this.pendingRecipe);
            this.pendingRecipe = null;
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.assistant-view-container {
  height: calc(100vh - 70px);
  display: flex;
  flex-direction: column;
  background: #f9fafb;
}

.assistant-hero {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 720px;
  width: 100%;
}

.hero-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 32px rgba(5, 47, 73, 0.3);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
}

.hero-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0 0 2rem 0;
  font-weight: 400;
}

.hero-form {
  width: 100%;
  margin-bottom: 2rem;
}

.input-wrapper {
  position: relative;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-wrapper:focus-within {
  border-color: #052f49;
  box-shadow: 0 4px 24px rgba(5, 47, 73, 0.15);
}

.hero-input {
  width: 100%;
  padding: 1rem 3.5rem 1rem 1.25rem;
  font-size: 1rem;
  border: none;
  border-radius: 16px;
  background: transparent;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  color: #111827;
  min-height: 56px;
  max-height: 200px;
}

.hero-input::placeholder {
  color: #9ca3af;
}

.send-button {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: #e5e7eb;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.send-button.active {
  background: #052f49;
  color: white;
}

.send-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.send-button:not(:disabled):hover {
  transform: scale(1.05);
}

/* Hero Action Links */
.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.hero-action-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.125rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(5, 47, 73, 0.2);
}

.hero-action-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(5, 47, 73, 0.3);
}

.hero-action-link.secondary {
  color: #6b7280;
  background: white;
  border: 1px solid #e5e7eb;
  box-shadow: none;
}

.hero-action-link.secondary:hover {
  color: #052f49;
  border-color: #052f49;
  background: #f9fafb;
  box-shadow: 0 2px 8px rgba(5, 47, 73, 0.1);
}

.hero-action-link.secondary svg {
  color: #9ca3af;
}

.hero-action-link.secondary:hover svg {
  color: #08c1c8;
}

.assistant-chat-wrapper {
  flex: 1;
  min-height: 0;
}
</style>
